"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  LayoutDashboard,
  CheckSquare,
  Radio,
  BarChart2,
  Users,
  CalendarDays,
  DollarSign,
  Palette,
  Settings,
  Menu,
  X,
  LogOut,
} from "lucide-react";
import clsx from "clsx";
import type { Session } from "@/lib/auth";

const NAV_ITEMS = [
  { href: "/", label: "Dashboard", icon: LayoutDashboard },
  { href: "/queue", label: "Approval Queue", icon: CheckSquare },
  { href: "/live", label: "Live View", icon: Radio },
  { href: "/report", label: "Reports", icon: BarChart2 },
  { href: "/guests", label: "Guest Bench", icon: Users },
  { href: "/episodes", label: "Episode Planning", icon: CalendarDays },
  { href: "/creative", label: "Creative Studio", icon: Palette },
  { href: "/sponsorships", label: "Sponsorships", icon: DollarSign },
  { href: "/settings", label: "Settings", icon: Settings },
];

type SidebarProps = {
  initialSession: Session | null;
};

export default function Sidebar({ initialSession }: SidebarProps) {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const session = initialSession;

  const close = () => setMobileOpen(false);

  // Hide sidebar on the login page
  if (pathname === "/login") return null;

  return (
    <>
      {/* Mobile hamburger button */}
      <button
        onClick={() => setMobileOpen(true)}
        className="md:hidden fixed top-4 left-4 z-50 w-9 h-9 flex items-center justify-center rounded-lg bg-[#12121A] border border-white/10 text-[#F0EAD6]"
        aria-label="Open menu"
      >
        <Menu size={18} />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 z-40"
          onClick={close}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          "fixed inset-y-0 left-0 z-50 w-56 bg-[#12121A] border-r border-white/5 flex flex-col shrink-0 transition-transform duration-300",
          "md:static md:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Logo */}
        <div className="px-5 py-5 border-b border-white/5 flex items-center justify-between">
          <div>
            <span className="font-[family-name:var(--font-space)] font-semibold text-base text-[#F0EAD6]">
              TOP
            </span>
            <p className="text-[11px] text-[#6B7280] mt-0.5">
              Producer console
            </p>
          </div>
          <button
            onClick={close}
            className="md:hidden text-[#6B7280] hover:text-[#F0EAD6]"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 px-3 space-y-1">
          {NAV_ITEMS.map(({ href, label, icon: Icon }) => {
            const active =
              href === "/" ? pathname === "/" : pathname.startsWith(href);
            return (
              <Link
                key={href}
                href={href}
                onClick={close}
                className={clsx(
                  "flex items-center gap-3 px-3 py-2 rounded-md text-sm border-l-2 transition-colors",
                  active
                    ? "border-[#D4AF37] text-[#F0EAD6] bg-white/[0.03] font-medium"
                    : "border-transparent text-[#6B7280] hover:text-[#F0EAD6] hover:bg-white/[0.03]"
                )}
              >
                <Icon size={16} />
                {label}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-white/5 space-y-3">
          {session && (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-[#F0EAD6] font-medium">{session.username}</p>
                <p className="text-[10px] text-[#6B7280]">{session.role}</p>
              </div>
              <form action="/api/auth/logout" method="post">
                <button
                  type="submit"
                  className="text-[#6B7280] hover:text-red-400 transition-colors"
                  aria-label="Sign out"
                >
                  <LogOut size={15} />
                </button>
              </form>
            </div>
          )}
          <p className="text-[10px] text-[#6B7280]">v1.0 · Phase 1</p>
        </div>
      </aside>
    </>
  );
}
